/**
 * BulkAddDuplicateChoice — Block 2 per-row duplicate-resolution affordance
 * (Block 6.E).
 *
 * Surfaces top-3 candidates per row that might be duplicates. Operator
 * picks "Yes, same record (skip add)" → choice carries a duplicate_of_org_id;
 * disposition flips to 'skip_as_duplicate'. Or "No, separate record" →
 * disposition stays 'create'.
 */

import type { ReactNode } from 'react';
import type { BulkAddDuplicateCandidate } from '@/lib/types';

export interface BulkAddDuplicateChoiceProps {
  rowIndex: number;
  /** Pasted name from the row. */
  pastedName: string;
  candidates: ReadonlyArray<BulkAddDuplicateCandidate>;
  /** Current decision: undefined = unmade; null = "no separate record"; string = candidate id. */
  decision: string | null | undefined;
  onDecide: (decision: { duplicateOfOrgId: string | null }) => void;
}

export function BulkAddDuplicateChoice({
  rowIndex,
  pastedName,
  candidates,
  decision,
  onDecide,
}: BulkAddDuplicateChoiceProps): ReactNode {
  const groupName = `bulk-add-dup-row-${rowIndex}`;
  return (
    <div className="border border-amber-200 bg-amber-50 rounded-md p-3 mb-2">
      <div className="text-sm mb-2">
        <span className="font-semibold">"{pastedName}"</span> may be the same as:
      </div>
      <ul className="space-y-2 mb-2">
        {candidates.map((cand) => {
          const id = `${groupName}-${cand.id}`;
          return (
            <li key={cand.id} className="text-sm">
              <label className="flex items-baseline gap-2 cursor-pointer min-h-11">
                <input
                  id={id}
                  type="radio"
                  name={groupName}
                  className="mt-0.5"
                  checked={decision === cand.id}
                  onChange={() => onDecide({ duplicateOfOrgId: cand.id })}
                />
                <span className="flex-1">
                  <span className="font-semibold">{cand.name}</span>
                  {cand.address && <span className="text-ink-soft"> · {cand.address}</span>}
                  <span className="text-xs text-ink-soft ml-1">
                    ({(cand.similarity * 100).toFixed(0)}% match)
                  </span>
                </span>
              </label>
            </li>
          );
        })}
        <li className="text-sm">
          <label className="flex items-baseline gap-2 cursor-pointer min-h-11">
            <input
              type="radio"
              name={groupName}
              className="mt-0.5"
              checked={decision === null}
              onChange={() => onDecide({ duplicateOfOrgId: null })}
            />
            <span>No, separate record</span>
          </label>
        </li>
      </ul>
    </div>
  );
}
